import classnames from 'classnames';
import _ from 'lodash';
import moment from 'moment';

/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types';

import React from 'react';
import Localized from '../../../shared/components/Localized';
import './Timeline.scss';
import TimelineItem from './TimelineItem';

export default class TimelineRow extends React.Component {
  static propTypes = {
    serviceKey: PropTypes.string,
    incidents: PropTypes.array,
    maintenances: PropTypes.array,
    range: PropTypes.object,
    offset: PropTypes.object
  };
  render() {
    const { serviceKey, incidents, maintenances, range, offset } = this.props;

    const now = moment().subtract(offset);
    const cutoff = moment(now).subtract(range);

    const incidentImpacts = _.flatten(
      incidents.map(incident => {
        // stort incident impacts by start time
        const sortedImpacts = incident.IncidentImpacts.sort((a, b) =>
          b.startTime.diff(a.startTime)
        );
        return sortedImpacts.map((incidentImpact, index) => {
          // start and end time for impact
          const start = moment(incidentImpact.startTime);
          const end = moment.isMoment(incidentImpact.endTime)
            ? incidentImpact.endTime
            : moment();

          // exits if not on the timeline
          if (end.isBefore(cutoff) || start.isAfter(now)) return null;

          const right =
            _.clamp((now.diff(end) / range.asMilliseconds()) * 100, 0, 100) +
            '%';
          const left =
            _.clamp(
              (start.diff(cutoff) / range.asMilliseconds()) * 100,
              0,
              100
            ) + '%';

          return (
            <TimelineItem
              key={`${incident.id}_${index}`}
              incidentId={incident.id}
              right={right}
              left={left}
              severity={incidentImpact.severity}
              isFirst={index === 0}
              isLast={index === sortedImpacts.length - 1}
            />
          );
        });
      })
    );

    const maintenanceImpacts = _.flatten(
      maintenances.map(maintenance => {
        // stort incident impacts by start time
        const sortedImpacts = maintenance.MaintenanceImpacts.sort((a, b) =>
          b.startTime.diff(a.startTime)
        );
        return sortedImpacts.map((maintenanceImpact, index) => {
          // start and end time for impact
          const start = moment(maintenanceImpact.startTime);
          const end = moment.isMoment(maintenanceImpact.endTime)
            ? maintenanceImpact.endTime
            : moment();

          // exits if not on the timeline
          if (end.isBefore(cutoff) || start.isAfter(now)) return null;

          const right =
            _.clamp((now.diff(end) / range.asMilliseconds()) * 100, 0, 100) +
            '%';
          const left =
            _.clamp(
              (start.diff(cutoff) / range.asMilliseconds()) * 100,
              0,
              100
            ) + '%';

          return (
            <TimelineItem
              key={maintenance.id}
              maintenanceId={maintenance.id}
              right={right}
              left={left}
              severity={maintenanceImpact.severity}
              isFirst={index === 0}
              isLast={index === sortedImpacts.length - 1}
            />
          );
        });
      })
    );

    // concatenated incident and maitnenance impacts
    const impacts = [...incidentImpacts, ...maintenanceImpacts];

    const container = {
      height: '5rem'
    };

    const timelineRowClasses = classnames({
      'slds-grid': true,
      'slds-nowrap': true,
      'timeline-row': true,
      'timeline-row--now': offset.asMilliseconds() === 0
    });

    const healthyIconClasses = classnames({
      'slds-icon__container': true,
      'timeline-row-healthy-icon': true,
      'slds-hide': offset.asHours() !== 0
    });

    return (
      <div className="slds-col slds-size--1-of-1">
        <div className={timelineRowClasses} style={container}>
          <div className="slds-col timeline-row-body">
            <div className="timeline-row-body-wrapper">
              <div className="timeline-row-healthy" />
              <div className={healthyIconClasses}>
                <svg aria-hidden="true">
                  <use xlinkHref="/assets/icons/action-sprite/svg/symbols.svg#approval" />
                </svg>
              </div>
              {impacts}
            </div>
          </div>
          <div className="slds-col timeline-row-label">
            <Localized
              modelName="service"
              modelAttribute="label"
              modelKey={serviceKey}
            />
          </div>
        </div>
      </div>
    );
  }
}
